/**
 * Whole-number arithmetic on BigInt, for the calculators whose answers must be exact.
 *
 * Constants are built with `BigInt(…)` rather than written as `0n` literals because the
 * project compiles against ES2017, where TypeScript rejects BigInt literal syntax.
 */

export const BIG_ZERO = BigInt(0);
export const BIG_ONE = BigInt(1);
export const BIG_TWO = BigInt(2);
export const BIG_TEN = BigInt(10);

/** A typed integer: optional sign (ASCII or Unicode minus), then digits only. */
const INTEGER_TOKEN = /^[+\-−]?\d+$/;

export function absBig(n: bigint): bigint {
  return n < BIG_ZERO ? -n : n;
}

export function minBig(a: bigint, b: bigint): bigint {
  return a < b ? a : b;
}

/** Greatest common divisor of |a| and |b|; gcd(0, 0) is 0. */
export function gcdBig(a: bigint, b: bigint): bigint {
  let x = absBig(a);
  let y = absBig(b);
  while (y !== BIG_ZERO) {
    const remainder = x % y;
    x = y;
    y = remainder;
  }
  return x;
}

/** Least common multiple of |a| and |b|; 0 when either is 0. */
export function lcmBig(a: bigint, b: bigint): bigint {
  if (a === BIG_ZERO || b === BIG_ZERO) {
    return BIG_ZERO;
  }
  return absBig((a / gcdBig(a, b)) * b);
}

/**
 * ⌊√n⌋ for n ≥ 0 by Newton's method.
 *
 * Starts from a power of two that is guaranteed to be at least √n, from which the
 * integer Newton step decreases monotonically to the floor of the root — no float
 * guess, so it stays exact for numbers far beyond 2^53.
 */
export function isqrtBig(n: bigint): bigint {
  if (n < BIG_ZERO) {
    throw new RangeError("isqrtBig of a negative number");
  }
  if (n < BIG_TWO) {
    return n;
  }
  const bits = n.toString(2).length;
  let x = BIG_TWO ** BigInt(Math.ceil(bits / 2));
  let next = (x + n / x) / BIG_TWO;
  while (next < x) {
    x = next;
    next = (x + n / x) / BIG_TWO;
  }
  return x;
}

export function isPerfectSquareBig(n: bigint): boolean {
  if (n < BIG_ZERO) {
    return false;
  }
  const root = isqrtBig(n);
  return root * root === n;
}

/** base^exponent mod modulus, by repeated squaring. */
export function modPowBig(
  base: bigint,
  exponent: bigint,
  modulus: bigint
): bigint {
  if (modulus === BIG_ONE) {
    return BIG_ZERO;
  }
  let result = BIG_ONE;
  let square = ((base % modulus) + modulus) % modulus;
  let remaining = exponent;
  while (remaining > BIG_ZERO) {
    if (remaining % BIG_TWO === BIG_ONE) {
      result = (result * square) % modulus;
    }
    remaining /= BIG_TWO;
    square = (square * square) % modulus;
  }
  return result;
}

/** 10^k as a BigInt. */
export function pow10Big(k: number): bigint {
  return BIG_TEN ** BigInt(k);
}

/** Read one typed integer exactly, or null. Accepts the Unicode minus sign. */
export function parseBigIntToken(token: string): bigint | null {
  const trimmed = token.trim();
  if (!INTEGER_TOKEN.test(trimmed)) {
    return null;
  }
  return BigInt(trimmed.replace("−", "-").replace("+", ""));
}

/** An integer as the reader sees it in working: digits, with a real minus sign. */
export function formatBig(n: bigint): string {
  return n < BIG_ZERO ? `−${(-n).toString()}` : n.toString();
}
